import { RentalTimeSection, ShortTermPaymentSection, VehicleSection } from './ShortTermDetails'
import { selectWarehouse } from 'containers/Warehouses/warehousesSlice'
import DateTile from 'containers/OrderDetails/Calendar/LongTerm/DateTile'
import Schedule from 'containers/OrderDetails/Summary/Schedule'
import { ReactComponent as Arrow } from 'assets/arrowDown.svg'
import { Navigate } from 'hooks/useNavigateWithScrollSave'
import ArrowBtn from 'components/buttons/ArrowBtn'
import useTranslation from 'hooks/useTranslation'
import InvoicesSection from './InvoicesSection'
import DepositsSection from './DepositsSection'
import { useParams } from 'react-router-dom'
import { useReduxSelector } from 'store'
import { toLongDate } from 'utils/date'
import Error from 'components/Error'
import { forwardRef } from 'react'
import { SECTION_TYPES } from '..'

interface MagDetailsProps {
  navigate: Navigate
}

const MagDetails = forwardRef<HTMLDivElement, MagDetailsProps>(({ navigate }, ref) => {

  const { id } = useParams<warehouse.PageParams>()
  const warehouse = useReduxSelector(state => selectWarehouse(state, id)) as warehouse.Warehouse
  const { typeName, objectId, fullDateFrom, fullDateTo, size, placeName, placeAddress, clientName, clientSurname, clientAddress, contractNumber, contractPdf, contractSigned, isShared, permissions, canExtend, terminationTerms, resignation, schedule_list_details, needPayment, rentableType, isShortTerm } = warehouse

  const { t, lang } = useTranslation('warehouse')
  const openAction = (action: keyof typeof SECTION_TYPES) => {
    navigate(`/warehouses/${id}/${action}`)
  }

  return (
    <div className='menu__section summary'>
      {/* <p>{t('rentalDetails')}</p> */}

      <div
        ref={ref}
        className='menu__details'>

        {!isShortTerm && <div className='details__date calendar'>
          <div className='calendar__main'>
            <div className='calendar__inputs center'>
              <DateTile
                start
                date={fullDateFrom}
              />
              <DateTile
                date={resignation?.date || fullDateTo}
              />
            </div>
          </div>
        </div>}

        <div className='list'>
          {rentableType === 'car' && <VehicleSection warehouse={warehouse} />}
          {isShortTerm && <RentalTimeSection warehouse={warehouse} />}
          {rentableType !== 'car' && <div>
            <div className='list__header element__title'>{typeName}</div>
            <div className='list__element --big'>
              <div className='element__desc'>
                <div>{t('area')}: {Number(size?.area)}m²</div>
                {(size?.internalLength || size?.length) && <div>{t('length')}: {size?.internalLength || size?.length}m</div>}
                {(size?.internalWidth || size?.width) && <div>{t('width')}: {size?.internalWidth || size?.width}m</div>}
                {(size?.internalHeight || size?.height) && <div>{t('height')}: {size?.internalHeight || size?.height}m</div>}
              </div>
            </div>
          </div>}
          <div>
            <div className='list__header element__title'>{t('location')}</div>
            <div className='list__element --big'>
              <div className='group'>
                <div className='element__desc'>
                  <div>{placeName}</div>
                  {placeAddress && placeAddress.split(', ').map((text, index) => <div key={index}>{text}</div>)}
                </div>
                <div
                  className='element__desc --action'
                  onClick={() => {
                    window.open(`https://www.google.com/maps/search/?api=1&query=${placeAddress}`)
                  }}>
                  {t('show')}
                </div>
              </div>
            </div>
          </div>
          <div>
            <div className='list__header element__title'>{t('tenant')}</div>
            <div className='list__element --big'>
              <div>
                <div
                  className='element__title'
                  style={{ marginBottom: '0.5em' }}>
                  {clientName} {clientSurname}
                </div>
                <div className='element__desc'>
                  {clientAddress && clientAddress.split(', ').map((text, index) => <div key={index}>{text}</div>)}
                </div>
              </div>
            </div>
          </div>
          {(!isShared || permissions?.includes('contract')) && <div>
            <div className='list__header'>
              <div className='element__title'>{t('contract')}</div>
            </div>
            <div className='list__element --big'>
              <div className='element__desc'>
                <div className='group'>
                  <div className='element__invoice'>
                    <div><b>{t('contractNumber')}: {contractNumber}</b></div>
                    <div>{t(contractSigned ? 'contractSigned' : 'contractNotSigned')}</div>
                  </div>
                  {contractPdf && <Arrow
                    onClick={() => {
                      window.open(contractPdf, '_blank')
                    }} />}
                </div>
              </div>
            </div>
          </div>}
          {(!isShared || permissions?.includes('contract')) && resignation && <div>
            <div className='list__header element__title'>{t(`resignationType.${resignation.type}`)}</div>
            <div className='list__element --big'>
              <div className='group'>
                <div className='element__desc'>
                  <div>{t('endDate')}: {toLongDate(resignation.date, lang, true)}</div>
                  {resignation.reason && <div>{t('reason')}: {resignation.reason}</div>}
                  <div>{t('attachments')}: {resignation.documents.length > 0 ? resignation.documents.length === 1 ? t('onePhoto') : `${resignation.documents.length} ${t('photos')}` : <span className={resignation.needPhoto ? '--warning' : undefined}>{t('none')}</span>}</div>
                </div>
                {resignation.isCompleted
                  ? null
                  : <ArrowBtn
                    enlarged
                    text={t((resignation.documents.length === 0 && resignation.needPhoto) ? 'addPhoto' : 'edit')}
                    className={(resignation.documents.length === 0 && resignation.needPhoto) ? undefined : '--black'}
                    action={() => {
                      openAction(resignation.type as keyof typeof SECTION_TYPES)
                    }}
                  />}
              </div>
            </div>
          </div>}
          {(!isShared || permissions?.includes('payment')) && isShortTerm && <ShortTermPaymentSection
            warehouse={warehouse}
            onPay={() => {
              navigate(`/warehouses/${objectId}/invoices?selectAll=1`)
            }}
          />}
          {(!isShared || permissions?.includes('payment')) && !isShortTerm && <div>
            <div className='group'>
              <div className='list__header element__title'>{t('schedule')}</div>
              {needPayment
                ? <div
                  className='menu__more --secFont'
                  onClick={() => {
                    navigate(`/warehouses/${objectId}/invoices?selectAll=1`)
                  }}>{t('pay2')} →</div>
                : <div
                  className='menu__more --secFont'
                  onClick={() => {
                    navigate(`/account/payments/extra?warehouseId=${id}`)
                  }}>{t('makeOverpayment')} →</div>}
            </div>
            {schedule_list_details && schedule_list_details?.length > 0
              ? <div className='menu__paymentSchedule'>
                <Schedule schedule={schedule_list_details} />
              </div>
              : <div className='list__element --big'>
                <Error staticPos text={t('noSchedule')} />
              </div>}
          </div>}
          {(!isShared || permissions?.includes('invoice')) && <InvoicesSection
            navigate={navigate}
          />}
          {(!isShared || permissions?.includes('payment')) && <DepositsSection />}
        </div>

        {(!isShared || permissions?.includes('contract'))
          ? <div className='details__buttons'>
            {(canExtend && !resignation?.dateAccepted && ((resignation?.type === 'termination' && resignation?.date) || fullDateTo)) && <ArrowBtn
              enlarged
              text={t('extendYourRental')}
              action={() => {
                openAction('extension')
              }}
            />}
            {(terminationTerms?.canTerminate && !resignation) && <ArrowBtn
              enlarged
              text={t('terminateRental')}
              action={() => {
                openAction('termination')
              }}
            />}
            {(terminationTerms?.canCancel && !resignation) && <div
              className='button__cancel'
              onClick={() => {
                openAction('cancel')
              }}>
              {t('cancelRental')}
            </div>}
          </div>
          : <div className='details__buttons' />}
      </div>
    </div>
  )
})

export default MagDetails